import { useEffect, useState } from 'react';
import { SUBSCRIBER_STATUSES, type SubscriberDto, type SubscriberStatus } from '@ecom/contracts';
import { FilterBar } from '@/components/filters';
import { MailIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import { Alert, Button, ConfirmDialog, EmptyState, Pill, TableSkeleton, cx } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, date } from '@/lib/format';
import { useSubscribersStore } from '@/stores/subscribers.store';

const TONES = { confirmed: 'success', pending: 'warning', withdrawn: 'neutral' } as const;

/**
 * Marketing → Subscribers (ADR-099): every address that signed up for the
 * newsletter without an account — confirmed, waiting for its click, or
 * withdrawn — where it came from, and the way to export or withdraw it.
 */
export function SubscribersPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { items, counts, nextCursor, search, status, loading, exporting, error, load, loadMore, exportCsv, withdraw } = useSubscribersStore();
  const [withdrawing, setWithdrawing] = useState<SubscriberDto | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  const tabs: (SubscriberStatus | null)[] = [null, ...SUBSCRIBER_STATUSES];

  return (
    <Page
      title={t('subscribersPage')}
      subtitle={t('subscribersPageHint')}
      actions={
        <Button variant="secondary" disabled={exporting} onClick={() => void exportCsv()}>
          {exporting ? t('loading') : t('exportCsv')}
        </Button>
      }
      fill
    >
      <ListCard>
        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2 border-b border-line p-4">
          {tabs.map((tab) => (
            <button
              key={tab ?? 'all'}
              type="button"
              aria-pressed={status === tab}
              onClick={() => void load({ status: tab })}
              className={cx('rounded-full border px-3 py-1.5 text-sm', status === tab ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line text-ink hover:border-brand-300')}
            >
              {t(`subscriberStatus_${tab ?? 'all'}`)}
              {counts ? (
                <span data-numeric className="ms-1.5 text-xs text-ink-muted">
                  {count(tab ? counts[tab] : counts.confirmed + counts.pending + counts.withdrawn, locale)}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <ListToolbar>
          <FilterBar search={search} onSearch={(term) => void load({ search: term })} placeholder={t('subscribersSearch')} shown={items.length} />
        </ListToolbar>
        {loading && items.length === 0 ? (
          <TableSkeleton columns={5} />
        ) : items.length === 0 ? (
          <EmptyState title={t('subscribersEmpty')} body={t('subscribersEmptyHint')} icon={<MailIcon />} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('email')}</Th>
                  <Th>{t('status')}</Th>
                  <Th>{t('subscriberSource')}</Th>
                  <Th>{t('subscriberSignedUp')}</Th>
                  <Th align="end">
                    <span className="sr-only">{t('moreActions')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {items.map((subscriber) => (
                  <Tr key={subscriber.id}>
                    <Td>
                      <span dir="ltr" className="block text-start font-medium text-ink">
                        {subscriber.email}
                      </span>
                      <span className="block text-xs text-ink-muted">
                        {t(`feedLanguage_${subscriber.locale}`)}
                        {subscriber.hasAccount ? ` · ${t('subscriberHasAccount')}` : ''}
                      </span>
                    </Td>
                    <Td>
                      <Pill tone={TONES[subscriber.status]}>{t(`subscriberStatus_${subscriber.status}`)}</Pill>
                    </Td>
                    <Td className="text-sm text-ink-muted">{t(`subscriberSource_${subscriber.source}`)}</Td>
                    <Td className="whitespace-nowrap text-sm text-ink-muted">
                      {date(subscriber.consentAt, locale)}
                      {subscriber.confirmedAt ? <span className="block text-xs">{t('subscriberConfirmedOn', { date: date(subscriber.confirmedAt, locale) })}</span> : null}
                    </Td>
                    <Td align="end">
                      {subscriber.status !== 'withdrawn' ? (
                        <Button variant="ghost" size="sm" onClick={() => setWithdrawing(subscriber)}>
                          {t('subscriberWithdraw')}
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>
      <ConfirmDialog
        open={withdrawing !== null}
        title={t('subscriberWithdrawTitle')}
        body={t('subscriberWithdrawBody', { email: withdrawing?.email ?? '' })}
        confirmLabel={t('subscriberWithdraw')}
        cancelLabel={t('cancel')}
        tone="danger"
        onCancel={() => setWithdrawing(null)}
        onConfirm={() => {
          if (withdrawing) void withdraw(withdrawing.id);
          setWithdrawing(null);
        }}
      />
    </Page>
  );
}
